import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import TournamentIdPicker from './TournamentIdPicker';
import GameCardList from './GameCardList';
import TournamentOverallActions from './TournamentOverallActions';
import TournamentInfo from './TournamentInfo';
import { getBaseUrl } from '../utils/apiUtils';
import { formatDate } from '../utils/dateUtils';
import './GameCard.css';
import './MainContent.css';
import {useTournamentAppConfiguration} from "../utils/tournamentconfig/TournamentConfiguration"
import NavigationTabs from './NavigationTabs';
import FriendsView from './FriendsView';
import ConciergeView from './ConciergeView';
import { ResultsContext } from '../contexts/ResultsContext';

function MainContent() {
  const tournamentAppConfiguration = useTournamentAppConfiguration()
  const { results, fetchResults, lastFetchTime, username, setUsername, tournamentId, setTournamentIdOnly } = useContext(ResultsContext);
  const [activeTab, setActiveTab] = useState('tournament');
  const [sortOrder, setSortOrder] = useState('default');

  // Derive tournament info from results
  const tournamentInfo = useMemo(() => {
    if (!results) return null;
    return {
      title: results.title,
      tournamentId: results.tournamentId,
      type: results.type,
      start: results.start,
      end: results.end,
      lastFetched: results.lastFetched,
      minutesBetweenFetches: results.minutesBetweenFetches
    };
  }, [results]);

  // Fetch the most recent tournament ID, prioritizing currently running tournaments
  const fetchMostRecentTournamentId = useCallback(async () => {

    try {
      const response = await fetch(`${getBaseUrl()}${tournamentAppConfiguration.endPoints.tournamentsList}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.tournaments && data.tournaments.length > 0) {
          const now = new Date();
          
          // First, check for currently running tournaments
          const currentlyRunningTournaments = data.tournaments.filter(tournament => {
            const startDate = tournament.start ? new Date(tournament.start) : null;
            const endDate = tournament.end ? new Date(tournament.end) : null;
            
            // A tournament is current if today is between its start and end dates
            return (startDate && endDate && now >= startDate && now <= endDate) || // Both dates available
                  (startDate && !endDate && now >= startDate) || // Only start date available
                  (!startDate && endDate && now <= endDate); // Only end date available
          });
          
          if (currentlyRunningTournaments.length > 0) {
            // Sort currently running tournaments by ID (highest first)
            const sortedCurrentTournaments = [...currentlyRunningTournaments].sort((a, b) => {
              return parseInt(b.tournamentId) - parseInt(a.tournamentId);
            });
            
            // Return the highest ID of currently running tournaments
            return sortedCurrentTournaments[0].tournamentId.toString();
          }
          
          // If no currently running tournaments, fall back to the highest tournament ID
          const sortedByIdTournaments = [...data.tournaments].sort((a, b) => {
            return parseInt(b.tournamentId) - parseInt(a.tournamentId);
          });
          
          // Return the highest tournament ID
          if (sortedByIdTournaments.length > 0) {
            return sortedByIdTournaments[0].tournamentId.toString();
          }
        }
      } else {
        console.error('Failed to fetch tournaments:', response.status);
      }
    } catch (error) {
      console.error('Error fetching most recent tournament ID:', error);
    }
    return null;
  }, [tournamentAppConfiguration.endPoints.tournamentsList]);
  
  // Load saved tournament ID from localStorage or URL on component mount
  useEffect(() => {
    const loadInitialData = async () => {
      // Check URL parameters first
      const urlParams = new URLSearchParams(window.location.search);
      const urlTournamentId = urlParams.get('tournamentId');
      
      // If tournament ID is in URL or localStorage, use it
      if (urlTournamentId || localStorage.getItem('tournamentNumber')) {
        const savedTournamentId = localStorage.getItem('tournamentNumber');
        setTournamentIdOnly(urlTournamentId || savedTournamentId || '');
      } else {
        // Otherwise fetch the most recent tournament ID
        const mostRecentId = await fetchMostRecentTournamentId();
        if (mostRecentId) {
          setTournamentIdOnly(mostRecentId);
        }
      }
    };
    
    loadInitialData();
  }, [fetchMostRecentTournamentId, setTournamentIdOnly]);
  
  // Handle search submission from other components
  const handleSearch = useCallback((searchUsername, searchTournamentId) => {
    setUsername(searchUsername);
    setTournamentIdOnly(searchTournamentId);
  }, [setUsername, setTournamentIdOnly]);
  
  // Expose the update function globally so it can be called from TournamentOverallActions
  useEffect(() => {
    window.updateSearchUsername = handleSearch;
    
    // Clean up the global function when component unmounts
    return () => {
      delete window.updateSearchUsername;
    };
  }, [handleSearch]);
  
  return (
    <main className="main-content">
      {/* Search and Tournament Info - Consistent across all tabs */}
      <div className="top-section">
        <TournamentIdPicker
          value={tournamentId}
          onChange={setTournamentIdOnly}
          showTrigger={false}
        />

        {tournamentInfo && (
          <div className="tournament-info-container">
            <TournamentInfo 
              tournamentInfo={tournamentInfo} 
              formatDate={formatDate}
              lastFetchTime={lastFetchTime}
              onRefresh={() => fetchResults(tournamentId)}
              sortOrder={sortOrder}
              onSortChange={setSortOrder}
            />
          </div>
        )}
      </div>

      <NavigationTabs activeTab={activeTab} onTabChange={setActiveTab} />

      {activeTab === 'tournament' && (
        <>
          {/* Game Card List Component */}
          <GameCardList 
            username={username} 
            tournamentId={tournamentId} 
            sortOrder={sortOrder}
            onSortChange={setSortOrder}
          />
          {/* Tournament Overall Actions Component */}
          <TournamentOverallActions 
            tournamentId={tournamentId}
            currentUsername={username}
          />

          {/* Links Card Component */}
          <div className="links-container">
            {tournamentAppConfiguration.linksCard(tournamentId)}
            {/*<LinksCard tournamentId="ssss" />*/}
          </div>
        </>
      )}

      {activeTab === 'friends' && (
        <FriendsView 
          username={username} 
          tournamentId={tournamentId} 
          sortOrder={sortOrder} 
        />
      )}
      {activeTab === 'concierge' && <ConciergeView username={username} tournamentId={tournamentId} />}
    </main>
  );
}

export default MainContent;